import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ErrorBar, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import type { EventFacts } from '../factsApi';
import { bigOf, fmtInt, fmtPct } from '../engine';
import { COMPARE_METRICS, GROUP_COLORS, MAX_GROUPS, differenceOf, groupStats, type CompareKey, type CompareMetricDef, type GroupStats } from '../stats';

export interface CompareGroupInput {
  key: string;
  label: string;
  facts: EventFacts[];
}

/**
 * La comparación entre grupos de eventos: uno por columna, una métrica por
 * fila, cada cifra con su base y su margen, y frente al grupo de referencia
 * la diferencia con su test. Arriba, la métrica elegida como barras con su
 * margen de error, y debajo las lecturas en una frase cada una, que es lo
 * que se lleva a una reunión.
 *
 * Sirve igual para los eventos de un proyecto (cada evento es un grupo) que
 * para cualquier agrupación de Insights (proyectos, etiquetas o conjuntos a
 * medida). La estadística vive en `stats.ts`; aquí sólo se pinta.
 */
export function CompareMatrix({ groups: inputs, className }: { groups: CompareGroupInput[]; className?: string }) {
  const t = useT();
  const [baselineKey, setBaselineKey] = useState<string | null>(null);
  const [metricKey, setMetricKey] = useState<CompareKey>('leadRate');

  const groups = useMemo<GroupStats[]>(
    () => inputs.slice(0, MAX_GROUPS).map((g, i) => groupStats(g.key, g.label, GROUP_COLORS[i]!, g.facts)),
    [inputs],
  );
  const baseline = groups.find((g) => g.key === baselineKey) ?? groups[0] ?? null;
  const def = COMPARE_METRICS.find((m) => m.key === metricKey) ?? COMPARE_METRICS[0]!;

  if (groups.length < 2 || !baseline) {
    return (
      <div className={cn('rounded-[14px] border border-dashed p-6 text-center text-sm', className)} style={{ borderColor: 'var(--color-dash-border-2)', color: 'var(--color-dash-fg-muted)' }}>
        {t('compare.needTwo')}
      </div>
    );
  }

  const chart = groups.map((g) => {
    const v = g.values[def.key];
    return {
      key: g.key,
      name: g.label,
      value: v.metric.value ?? 0,
      err: def.kind === 'proportion' ? (v.metric.moe ?? 0) : 0,
      pending: v.metric.state === 'PENDING',
      base: v.prop?.base ?? null,
      color: g.color,
    };
  });
  const fmtValue = (m: GroupStats['values'][CompareKey]['metric']) => bigOf(m, t('dash.pending'));
  const fmtDiff = (d: number, kind: CompareMetricDef['kind']) =>
    kind === 'proportion' ? `${d > 0 ? '+' : ''}${d.toFixed(1)} pts` : kind === 'usd' ? `${d > 0 ? '+' : ''}$${Math.abs(d).toFixed(2)}` : `${d > 0 ? '+' : ''}${d}`;

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {/* La referencia se elige en un desplegable: con muchos grupos una fila
          de fichas no cabe. Las fichas de al lado son la leyenda. */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('compare.baseline')}</span>
          <select
            value={baseline.key}
            onChange={(e) => setBaselineKey(e.target.value)}
            className="h-9 max-w-[340px] rounded-full border px-3.5 text-[13px] font-semibold focus:outline-none"
            style={{ background: 'var(--color-dash-ink)', color: 'var(--color-dash-lime)', borderColor: 'var(--color-dash-ink)' }}
          >
            {groups.map((g) => (
              <option key={g.key} value={g.key}>
                {g.label} · {t('compare.eventsN', { n: g.counts.events })}
              </option>
            ))}
          </select>
        </label>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {groups.map((g) => (
            <span key={g.key} className="flex items-center gap-1.5 text-xs" style={{ color: g.key === baseline.key ? 'var(--color-dash-fg)' : 'var(--color-dash-fg-muted)', fontWeight: g.key === baseline.key ? 700 : 500 }}>
              <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: g.color }} />
              {g.label}
            </span>
          ))}
        </span>
        {inputs.length > MAX_GROUPS && (
          <span className="text-xs" style={{ color: 'var(--color-dash-orange)' }}>{t('compare.capped', { n: MAX_GROUPS })}</span>
        )}
      </div>

      {/* La métrica del gráfico */}
      <div className="flex flex-wrap items-center gap-1.5">
        {COMPARE_METRICS.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setMetricKey(m.key)}
            className="rounded-full px-3 py-1.5 text-xs font-bold transition-colors"
            style={m.key === def.key ? { background: 'var(--color-dash-lime)', color: 'var(--color-dash-ink)' } : { background: 'var(--color-dash-paper-3)', color: 'var(--color-dash-fg-2)' }}
          >
            {t(`compare.metric.${m.key}`)}
          </button>
        ))}
      </div>

      <section className="rounded-[14px] border p-[22px]" style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-[19px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{t(`compare.metric.${def.key}`)}</h3>
          <span className="text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>
            {def.kind === 'proportion' ? t('compare.chartNoteProportion') : t('compare.chartNoteNoTest')}
          </span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 16, right: 12, bottom: 8, left: -8 }} barCategoryGap="28%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-dash-track)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--color-dash-fg-muted)' }} tickLine={false} axisLine={{ stroke: 'var(--color-dash-border)' }} interval={0} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-dash-fg-muted)' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => (def.kind === 'proportion' ? `${v}%` : def.kind === 'usd' ? `$${v}` : String(v))} />
              <Tooltip
                cursor={{ fill: 'var(--color-dash-paper-2)' }}
                content={({ active, payload }) => {
                  const p = payload?.[0]?.payload as (typeof chart)[number] | undefined;
                  if (!active || !p) return null;
                  return (
                    <div className="rounded-lg border px-3 py-2 text-xs shadow-md" style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)', color: 'var(--color-dash-fg)' }}>
                      <div className="font-bold">{p.name}</div>
                      <div style={{ color: 'var(--color-dash-fg-muted)' }}>
                        {p.pending ? t('dash.pending') : def.kind === 'proportion' ? `${fmtPct(p.value)} ± ${p.err} pts · n = ${fmtInt(p.base)}` : def.kind === 'usd' ? `$${p.value.toFixed(2)}` : String(p.value)}
                      </div>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={72} isAnimationActive={false}>
                {chart.map((c) => (
                  <Cell key={c.key} fill={c.pending ? 'var(--color-dash-gray)' : c.color} />
                ))}
                {def.kind === 'proportion' && <ErrorBar dataKey="err" width={6} strokeWidth={2} stroke="var(--color-dash-fg)" direction="y" />}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Las lecturas frente a la referencia, en una frase cada una. */}
        <ul className="mt-4 space-y-2 border-t pt-4 text-sm" style={{ borderColor: 'var(--color-dash-track)' }}>
          {groups.filter((g) => g.key !== baseline.key).map((g) => {
            const d = differenceOf(def, baseline, g);
            const a = baseline.values[def.key].metric;
            const b = g.values[def.key].metric;
            let text: string;
            if (d.diff == null || a.value == null || b.value == null) text = t('compare.read.pending', { b: g.label, a: baseline.label, metric: t(`compare.metric.${def.key}`) });
            else if (d.test) {
              const base = { b: g.label, a: baseline.label, metric: t(`compare.metric.${def.key}`), vb: fmtValue(b), va: fmtValue(a), diff: fmtDiff(d.diff, def.kind), lo: d.test.ciLow.toFixed(1), hi: d.test.ciHigh.toFixed(1), p: d.test.pValue < 0.001 ? '< 0.001' : d.test.pValue.toFixed(3) };
              text = d.test.significant ? t(d.verdict === 'better' ? 'compare.read.better' : 'compare.read.worse', base) : t('compare.read.same', base);
              if (d.test.directional) text += ` ${t('compare.read.directional')}`;
            } else text = t('compare.read.noTest', { b: g.label, a: baseline.label, metric: t(`compare.metric.${def.key}`), vb: fmtValue(b), va: fmtValue(a), diff: fmtDiff(d.diff, def.kind) });
            return (
              <li key={g.key} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: g.color }} />
                <span style={{ color: 'var(--color-dash-fg-2)' }}>{text}</span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* La matriz entera */}
      <section className="overflow-x-auto rounded-[14px] border" style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr style={{ background: 'var(--color-dash-paper-3)' }}>
              <th className="px-4 py-3 text-left text-[10px] font-extrabold uppercase tracking-wider" style={{ color: 'var(--color-dash-fg-subtle)' }}>{t('compare.col.metric')}</th>
              {groups.map((g) => (
                <th key={g.key} className="px-4 py-3 text-right text-[10px] font-extrabold uppercase tracking-wider" style={{ color: g.key === baseline.key ? 'var(--color-dash-fg)' : 'var(--color-dash-fg-subtle)' }}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-[2px]" style={{ background: g.color }} />
                    {g.label}
                    {g.key === baseline.key && <span className="dash-mono normal-case tracking-normal" style={{ color: 'var(--color-dash-olive)' }}>· {t('compare.ref')}</span>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t" style={{ borderColor: 'var(--color-dash-track)' }}>
              <td className="px-4 py-2.5 font-semibold" style={{ color: 'var(--color-dash-fg)' }}>{t('compare.col.context')}</td>
              {groups.map((g) => (
                <td key={g.key} className="px-4 py-2.5 text-right text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>
                  {t('compare.context', { events: g.counts.events, interactions: fmtInt(g.counts.interactions), contacts: fmtInt(g.counts.contacts), leads: fmtInt(g.counts.leads), delivered: fmtInt(g.counts.delivered) })}
                </td>
              ))}
            </tr>
            {COMPARE_METRICS.map((m) => (
              <tr key={m.key} className={cn('border-t', m.key === def.key && 'bg-[var(--color-dash-paper-2)]')} style={{ borderColor: 'var(--color-dash-track)' }}>
                <td className="px-4 py-2.5">
                  <button type="button" onClick={() => setMetricKey(m.key)} className="text-left font-semibold hover:underline" style={{ color: 'var(--color-dash-fg)' }}>{t(`compare.metric.${m.key}`)}</button>
                  <div className="text-[11px]" style={{ color: 'var(--color-dash-fg-subtle)' }}>{t(`compare.metricNote.${m.key}`)}</div>
                </td>
                {groups.map((g) => {
                  const v = g.values[m.key];
                  const isBase = g.key === baseline.key;
                  const d = isBase ? null : differenceOf(m, baseline, g);
                  const pending = v.metric.state === 'PENDING';
                  return (
                    <td key={g.key} className="px-4 py-2.5 text-right align-top">
                      <div className="dash-mono text-base font-bold tabular-nums" style={{ color: pending ? 'var(--color-dash-orange)' : 'var(--color-dash-fg)' }}>{fmtValue(v.metric)}</div>
                      <div className="text-[11px]" style={{ color: 'var(--color-dash-fg-subtle)' }}>
                        {pending
                          ? v.metric.needs ? t('dash.needs', { what: t(`dash.need.${v.metric.needs}`, { n: v.metric.needsCount ?? 0 }) }) : ''
                          : m.kind === 'proportion' ? t('dash.basis', { n: (v.prop?.base ?? 0).toLocaleString(), moe: v.metric.moe ?? 0 }) : ''}
                      </div>
                      {d && d.diff != null && (
                        <span
                          className="dash-mono mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold"
                          title={d.test ? `z = ${d.test.z} · p = ${d.test.pValue} · IC95 [${d.test.ciLow}, ${d.test.ciHigh}]` : undefined}
                          style={
                            d.verdict === 'better'
                              ? { background: 'var(--color-dash-lime-soft)', color: 'var(--color-dash-olive)' }
                              : d.verdict === 'worse'
                                ? { background: 'var(--color-dash-orange-soft)', color: 'var(--color-dash-orange)' }
                                : { background: 'var(--color-dash-paper-3)', color: 'var(--color-dash-fg-muted)' }
                          }
                        >
                          {fmtDiff(d.diff, m.kind)}
                          {d.test ? (d.test.significant ? ` · p ${d.test.pValue < 0.001 ? '< 0.001' : `= ${d.test.pValue.toFixed(3)}`}` : ` · ${t('compare.ns')}`) : ''}
                          {d.test?.directional ? ' · ~' : ''}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="px-4 py-3 text-[11px] [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-subtle)' }}>{t('compare.footnote')}</p>
      </section>
    </div>
  );
}
